// Copyright 2025-2026 - Hewlett Packard Enterprise Company
import React, { useContext, useEffect, useMemo, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  Form,
  FormField,
  Text,
  Layer,
  TextArea,
  Button,
  DataTable,
  ThumbsRating,
} from 'grommet';
import PropTypes from 'prop-types';
import { GenAIFill } from 'grommet-icons/icons/GenAIFill';
import useFlags from '../../hooks/useFlags';
import { sendChatFeedback } from '../../actions/Api';
import TextEmphasis from '../TextEmphasis/TextEmphasis';
import MarkdownComponent from '../MarkdownComponent/MarkdownComponent';
import MessageLoader from './MessageLoader';
import { ChatContext } from '../../providers/ChatProvider';
import ChatQuestionButton from './ChatQuestionButton';
import { handleNavClick } from '../../utils/navigationHelpers';
import DeviceDetailsLink from '../DeviceDetailsLink/DeviceDetailsLink';
import GroupDetailsLink from '../GroupDetailPage/GroupDetailsLink';
import ServerSettingsDetailsLink from '../ServerSettingsDetailsLink/ServerSettingsDetailsLink';
import { UNMATCHED_CHAT_VALUE } from '../../constants/constants';

const ChatAnswer = ({ query, answer, showFollowUpQuestions, showFeedback }) => {
  const { t } = useTranslation('common');
  const history = useHistory();
  const flags = useFlags();
  const chatFilterLinksFlag =
    flags.T_20260223_116385_SERVER_FILTER_LINKS_CHATBOT_SUPPORT_BOOL();
  const {
    handleQueryClick,
    hideAllFollowUpQuestions,
    setIsFeedbackOpen,
    hideAllFeedback,
  } = useContext(ChatContext);

  const [showFeedbackPopup, setShowFeedbackPopup] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [feedbackId, setFeedbackId] = useState(null); // Store feedback_id
  const [isLiked, setIsLiked] = useState(null);
  const [feedbackLocked, setFeedbackLocked] = useState(false);
  const [showAdditionalFeedback, setShowAdditionalFeedback] = useState(false);
  const feedbackPopupRef = useRef(null);

  useEffect(() => {
    if (showFeedbackPopup && feedbackPopupRef.current) {
      feedbackPopupRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    }
  }, [showFeedbackPopup]);

  const chatResponse = useMemo(() => {
    if (answer?.summary === 'Error retrieving answer') {
      return { summary: t('chat.chat_error'), data: [] };
    }
    if (answer !== 'Loading') {
      return answer;
    }
    return { summary: '', items: [] };
  }, [answer, t]);

  const handleLinkError = error => {
    if (error?.response?.status === 403) {
      history.replace(`/error/${error.response.status}`);
    }
  };

  const handleFeedbackClick = async feedbackType => {
    setIsLiked(feedbackType === 1);
    try {
      const response = await sendChatFeedback(
        query,
        feedbackType,
        '',
        answer.id,
      ); // Send feedback
      if (response?.data?.feedback_id) {
        setFeedbackLocked(true);
        setFeedbackId(response.data.feedback_id);
      }
      if (feedbackType === -1) {
        setShowSuccessModal(true);
        setShowFeedbackPopup(true);
        setIsFeedbackOpen(true);
      } else if (feedbackType === 1) {
        setShowFeedbackPopup(false);
        setShowSuccessModal(true);
        setIsFeedbackOpen(false);
      }
    } catch (error) {
      setShowErrorModal(true);
    }
  };

  const closePopup = () => {
    setShowSuccessModal(true);
    setShowFeedbackPopup(false);
    setIsFeedbackOpen(false);
    setFeedback('');
  };

  const submitFeedback = async () => {
    try {
      if (feedbackId) {
        await sendChatFeedback(query, isLiked ? 1 : -1, feedback, feedbackId);
      } else {
        const response = await sendChatFeedback(
          query,
          isLiked ? 1 : -1,
          feedback,
          answer.id,
        );
        if (response?.data?.feedback_id) {
          setFeedbackId(response.data.feedback_id);
        }
      }
      setShowAdditionalFeedback(true);
      setShowSuccessModal(true);
      setFeedbackLocked(true);
    } catch (error) {
      setShowErrorModal(true);
    }
    setIsFeedbackOpen(false);
    closePopup();
  };

  const closeErrorModal = () => {
    setShowErrorModal(false);
  };

  const getFeedbackMessage = () => {
    if (showAdditionalFeedback) {
      return t('chat.feedback.additional_feedback_thank_you');
    }
    if (showSuccessModal) {
      return t('chat.feedback.thank_you_message');
    }
    return t('chat.chat_feedback_text');
  };

  const handleRowData = item => {
    if (typeof item === 'object' && item !== null && item?.id && item?.name) {
      if (item?.type === 'compute-ops/group') {
        return (
          <GroupDetailsLink
            key={item.id}
            groupId={item.id}
            name={item.name}
            handleError={handleLinkError}
            useButton
          />
        );
      }
      if (item?.type === 'compute-ops/server') {
        return (
          <DeviceDetailsLink
            key={item.id}
            deviceId={item.id}
            name={item.name}
            handleError={handleLinkError}
            useButton
          />
        );
      }
      if (item?.type === 'compute-ops-mgmt/setting') {
        return (
          <ServerSettingsDetailsLink
            key={item.id}
            id={item.id}
            name={item.name}
            handleError={handleLinkError}
            useButton
          />
        );
      }
      return (
        <Text size="small" truncate="tip">
          {item.name}
        </Text>
      );
    }
    if (typeof item === 'boolean') {
      return item ? (
        <Text size="small" truncate="tip">
          {t('chat.chat_boolean_true')}
        </Text>
      ) : (
        <Text size="small" truncate="tip">
          {t('chat.chat_boolean_false')}
        </Text>
      );
    }
    if (typeof item === 'string' || typeof item === 'number') {
      return (
        <Text size="small" truncate="tip">
          {item}
        </Text>
      );
    }
    // Fallback for unmatched conditions
    return (
      <Text size="small" truncate="tip">
        {UNMATCHED_CHAT_VALUE}
      </Text>
    );
  };

  return (
    answer && (
      <Box
        width="100%"
        direction="column"
        align="start"
        data-testid="chat-answer-outer-box"
        flex="grow"
      >
        <Box
          width="100%"
          direction="row"
          alignSelf="start"
          align="start"
          gap="xsmall"
          pad={{ right: 'xsmall', bottom: '0', left: '0' }}
          data-testid="chat-answer-inner-box"
          style={{ position: 'relative' }}
          flex="grow"
        >
          <Box direction="row" align="start" gap="xsmall">
            {answer === 'Loading' ? (
              <Box direction="row" gap="xsmall" margin={{ top: 'xsmall' }}>
                <GenAIFill color="brand" />
                <MessageLoader />
              </Box>
            ) : (
              <>
                <Box margin={{ top: 'xsmall' }}>
                  <GenAIFill color="brand" />
                </Box>
                <Box
                  pad={{ horizontal: 'xsmall', vertical: 'xsmall' }}
                  data-testid="chat-answer-summary-box"
                  flex
                >
                  <Text data-testid="chatTypographyElement">
                    {chatResponse.summary && (
                      <>
                        <MarkdownComponent
                          data-testid="chat-answer-markdown"
                          compact
                          enableLinks
                        >
                          {chatResponse.summary}
                        </MarkdownComponent>
                        {chatResponse?.serverFilter && chatFilterLinksFlag && (
                          <>
                            {chatResponse?.data?.rows?.length > 0 &&
                              chatResponse.data.rows.length < 10 &&
                              ` ${t('chat.showing_results_below', {
                                count: chatResponse.data.rows.length,
                              })}`}{' '}
                            {(() => {
                              const encoded = encodeURIComponent(
                                chatResponse.serverFilter,
                              );
                              const filterUrl = `/servers?serverFilter=${encoded}`;
                              const rowCount =
                                chatResponse?.data?.rows?.length ?? 0;
                              return (
                                <Button
                                  label={
                                    rowCount > 0 && rowCount < 10
                                      ? t('chat.view')
                                      : t('chat.view_all')
                                  }
                                  href={filterUrl}
                                  onClick={e => {
                                    handleNavClick(e, () => {
                                      history.push(filterUrl);
                                    });
                                  }}
                                  plain
                                  style={{ textDecoration: 'underline' }}
                                  data-testid="chat-filter-link"
                                />
                              );
                            })()}{' '}
                            {t('chat.on_server_page')}
                          </>
                        )}
                      </>
                    )}
                    {chatResponse?.data?.columns &&
                      chatResponse?.data?.rows &&
                      chatResponse?.data?.rows.length > 0 && (
                        <Box
                          overflow="auto"
                          margin={{ vertical: 'small' }}
                          style={{ width: 'fit-content', maxWidth: '100%' }}
                        >
                          <DataTable
                            pad={{ vertical: '5xsmall', horizontal: '3xsmall' }}
                            data={chatResponse?.data?.rows}
                            columns={chatResponse?.data?.columns?.map(
                              (column, index) => ({
                                property: column.name,
                                header: (
                                  <TextEmphasis>{column.name}</TextEmphasis>
                                ),
                                align:
                                  column.type === 'integer' ? 'end' : 'start',
                                render: datum => {
                                  /*
                                The data can be multiple shapes:
                                1. A nested array where each sub-array represents a 
                                   row with column values.
                                  Example: How many servers have auto ilo fw update 
                                           and how many don't have?
                                              [true, 41]       

                                2. An array of objects where name represents the resource 
                                   name(hostname/serial number), id represents the unique 
                                   identifier, and type can be any additional type info.
                                  Example: List all servers 
                                              {
                                                type: 'compute-ops/server',
                                                id: '944140-I98+8899944140298039',
                                                name: 'localhost'
                                              }

                                3. A combination of above cases.
                                  Example: Give me power state for all servers
                                              [
                                                { 
                                                  type: 'compute-ops/server', 
                                                  id: '168261-X99+8899168261599271', 
                                                  name: 'localhost.localdomain' 
                                                }, 'ON'
                                              ]
                              */
                                  if (Array.isArray(datum)) {
                                    const item = datum[index];
                                    return handleRowData(item);
                                  }
                                  if (
                                    typeof datum === 'object' &&
                                    datum !== null &&
                                    column.name in datum
                                  ) {
                                    return handleRowData(datum[column.name]);
                                  }
                                  return handleRowData(datum);
                                },
                              }),
                            )}
                          />
                        </Box>
                      )}
                  </Text>

                  {chatResponse?.summary &&
                    chatResponse?.followupQuestions &&
                    showFollowUpQuestions && (
                      <Box
                        gap="xsmall"
                        direction="column"
                        margin={{ top: 'xsmall' }}
                        data-testid="followup-questions-box"
                        responsive={false}
                      >
                        {chatResponse.followupQuestions.length > 0 &&
                          chatResponse.questionType === 'FOLLOW_UP' && (
                            <Text margin={{ bottom: '3xsmall' }}>
                              {t('chat.chat_follow_up')}
                            </Text>
                          )}
                        {chatResponse.followupQuestions.map(
                          (question, index) => (
                            <ChatQuestionButton
                              key={index}
                              question={question}
                              onClick={() => {
                                hideAllFollowUpQuestions();
                                hideAllFeedback();
                                handleQueryClick && handleQueryClick(question);
                              }}
                            />
                          ),
                        )}
                      </Box>
                    )}

                  {chatResponse.summary && showFeedback && (
                    <Box
                      gap="3xsmall"
                      direction="row"
                      margin={{ top: '3xsmall' }}
                    >
                      <ThumbsRating
                        onChange={click => {
                          if (click?.value === 'like') {
                            handleFeedbackClick(1);
                          } else if (click?.value === 'dislike') {
                            handleFeedbackClick(-1);
                          }
                        }}
                        disabled={feedbackLocked}
                        size="medium"
                      />
                      <Text
                        margin={{ left: 'xsmall', top: '3xsmall' }}
                        size="small"
                      >
                        {getFeedbackMessage()}
                      </Text>
                    </Box>
                  )}

                  {showFeedbackPopup && showFeedback && (
                    <Card
                      ref={feedbackPopupRef}
                      pad="medium"
                      margin={{ top: 'xsmall' }}
                      width="medium"
                      data-testid="inline-feedback-card"
                    >
                      <Text margin="none" size="large" color="text-strong">
                        {t('chat.feedback.additional_feedback')}
                      </Text>
                      <Form
                        messages={{
                          required: t('chat.feedback.required'),
                        }}
                        onSubmit={submitFeedback}
                      >
                        <FormField required name="feedback">
                          <TextArea
                            placeholder={t('chat.feedback.placeholder')}
                            value={feedback}
                            onChange={event => {
                              setFeedback(event.target.value);
                            }}
                            onKeyDown={event => {
                              if (event.key === 'Enter' && !event.shiftKey) {
                                event.preventDefault();
                                if (event.target.form) {
                                  event.target.form.requestSubmit();
                                }
                              }
                            }}
                            name="feedback"
                          />
                        </FormField>
                        <Box
                          direction="row"
                          gap="medium"
                          justify="start"
                          margin={{ top: 'xsmall' }}
                        >
                          <Button
                            label={t('chat.feedback.submit_btn')}
                            primary
                            type="submit"
                          />
                          <Button
                            label={
                              <Text weight="bold">
                                {t('chat.feedback.skip_btn')}
                              </Text>
                            }
                            onClick={() => {
                              setFeedbackLocked(true);
                              closePopup();
                            }}
                            plain
                          />
                        </Box>
                      </Form>
                    </Card>
                  )}
                </Box>
              </>
            )}
          </Box>
        </Box>
        {showErrorModal && ( // Error modal
          <Layer onEsc={closeErrorModal}>
            <Box pad="medium" gap="xsmall" width="medium">
              <TextEmphasis>{t('chat.feedback.error_message')}</TextEmphasis>
              <Text>{t('chat.feedback.try_again')}</Text>
              <Box direction="row" justify="end">
                <Button
                  primary
                  label={t('chat.feedback.close_btn')}
                  onClick={closeErrorModal}
                />
              </Box>
            </Box>
          </Layer>
        )}
      </Box>
    )
  );
};

export default ChatAnswer;

ChatAnswer.propTypes = {
  answer: PropTypes.any.isRequired,
};
